import React, { useEffect, useRef, useState } from 'react';
import { tagFromScan } from '../lib/assets';

/**
 * Read a device label's QR code with the camera.
 *
 * TWO READERS. Where the browser has its own BarcodeDetector - Chrome and
 * Edge on Android, macOS and ChromeOS - that is used. Where it does not -
 * Chrome and Edge on WINDOWS, Firefox, and Safari on an iPhone - frames from
 * the camera are read with jsQR instead. The first version used only
 * BarcodeDetector and hid the button without it, which meant the button
 * never appeared on the Windows laptops IT actually raises tickets from.
 * jsQR is loaded only when somebody opens the scanner, so a page that never
 * scans never downloads it.
 *
 * BARCODES (Lookup, 28 October 2026). With `barcodes` the scanner also reads
 * ordinary 1D barcodes and Data Matrix - a Dell service tag, the serial
 * sticker on a monitor. BarcodeDetector is used when it knows those formats;
 * otherwise zxing reads the frames, and is loaded only then. The stocktake
 * does not pass `barcodes` and reads exactly what it always did.
 *
 * On a laptop the camera faces the person, so the label is held up to it.
 *
 * The camera is switched off the moment a code is read, the dialog closes,
 * or the component goes away: a camera left running behind a form is a
 * light on somebody's laptop that nobody can explain.
 */
export const canScan = () => typeof window !== 'undefined'
  && Boolean(navigator.mediaDevices?.getUserMedia);

// The barcodes a service tag or a monitor's serial sticker comes in.
const BARCODE_FORMATS = ['qr_code', 'code_128', 'code_39', 'code_93', 'ean_13', 'ean_8',
  'upc_a', 'upc_e', 'itf', 'codabar', 'data_matrix'];

/** Draws a video frame to a canvas no wider than `max`; returns its pixels. */
function frameGrabber(max) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  return (video) => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    // Scaled down: a label fills enough of the frame at 640 wide, and a
    // smaller frame reads several times faster on a slow laptop.
    const scale = Math.min(1, max / w);
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  };
}

/** A function that reads one video frame, or null when nothing is found. */
async function makeReader(barcodes) {
  if ('BarcodeDetector' in window) {
    try {
      let formats = ['qr_code'];
      if (barcodes) {
        const have = (await window.BarcodeDetector.getSupportedFormats?.()) || [];
        formats = BARCODE_FORMATS.filter((f) => have.includes(f));
        if (!formats.includes('code_128')) throw new Error('no barcode formats here');
      }
      const detector = new window.BarcodeDetector({ formats });
      return async (video) => (await detector.detect(video))[0]?.rawValue || null;
    } catch { /* fall through to the library readers */ }
  }
  if (barcodes) {
    const Z = await import('@zxing/library');
    const hints = new Map();
    hints.set(Z.DecodeHintType.POSSIBLE_FORMATS, [
      Z.BarcodeFormat.QR_CODE, Z.BarcodeFormat.CODE_128, Z.BarcodeFormat.CODE_39, Z.BarcodeFormat.CODE_93,
      Z.BarcodeFormat.EAN_13, Z.BarcodeFormat.EAN_8, Z.BarcodeFormat.UPC_A, Z.BarcodeFormat.UPC_E,
      Z.BarcodeFormat.ITF, Z.BarcodeFormat.CODABAR, Z.BarcodeFormat.DATA_MATRIX]);
    hints.set(Z.DecodeHintType.TRY_HARDER, true);
    const reader = new Z.MultiFormatReader();
    reader.setHints(hints);
    const grab = frameGrabber(960);
    return async (video) => {
      const img = grab(video);
      if (!img) return null;
      const grey = new Uint8ClampedArray(img.width * img.height);
      for (let i = 0, j = 0; i < img.data.length; i += 4, j += 1) {
        grey[j] = (img.data[i] * 306 + img.data[i + 1] * 601 + img.data[i + 2] * 117) >> 10; // eslint-disable-line no-bitwise
      }
      try {
        const source = new Z.RGBLuminanceSource(grey, img.width, img.height);
        return reader.decode(new Z.BinaryBitmap(new Z.HybridBinarizer(source))).getText() || null;
      } catch { return null; } // no code in this frame
    };
  }
  const { default: jsQR } = await import('jsqr');
  const grab = frameGrabber(640);
  return async (video) => {
    const img = grab(video);
    return img ? jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })?.data || null : null;
  };
}

export default function AssetScanner({ onTag, onRaw, onCancel, barcodes = false }) {
  const video = useRef(null);
  // onRaw sees every code first (a shelf label, say); it answers true when it
  // took the code, otherwise the code is read as a device label as before.
  const rawRef = useRef(onRaw);
  rawRef.current = onRaw;
  const [error, setError] = useState('');

  useEffect(() => {
    let stream = null;
    let timer = null;
    let live = true;
    const stop = () => {
      if (timer) clearInterval(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    (async () => {
      try {
        const read = await makeReader(barcodes);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false
        });
        if (!live) { stop(); return; }
        video.current.srcObject = stream;
        await video.current.play().catch(() => {});
        let busy = false;
        timer = setInterval(async () => {
          if (busy || !video.current || video.current.readyState < 2) return;
          busy = true;
          try {
            const found = await read(video.current);
            if (found && live) {
              stop();
              if (rawRef.current && rawRef.current(found) === true) return;
              onTag(tagFromScan(found));
            }
          } catch { /* a frame it could not read; try the next one */ } finally {
            busy = false;
          }
        }, 250);
      } catch (e) {
        setError(e?.name === 'NotAllowedError'
          ? 'The camera was not allowed. Allow it in the browser’s address bar, or type the code into the box instead.'
          : e?.name === 'NotFoundError'
            ? 'This computer has no camera. Type the code into the box instead.'
            : 'The camera could not be started. Type the code into the box instead.');
      }
    })();
    return () => { live = false; stop(); };
  }, [onTag, barcodes]);

  return (
    <div className="as-scan">
      {error
        ? <p className="as-error" role="alert">{error}</p>
        : (
          <>
            <video ref={video} muted playsInline aria-label="Camera, pointed at the label" />
            <p className="as-quiet">
              {barcodes ? 'Point the camera at a Jarvis label or a barcode.' : 'Hold the device’s Jarvis label up to the camera.'}
            </p>
          </>
        )}
      <button type="button" className="btn btn-plain btn-small" onClick={onCancel}>Stop scanning</button>
    </div>
  );
}
